import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { acceptTerms } from '../../api/auth';
import { BlueHeader } from '../../components/ScreenShell';
import { LegalText } from '../../components/LegalText';
import { LegalDocOverlay } from '../../components/LegalDocOverlay';
import { haptics } from '../../telegram';
import { OFFER_TEXT } from './offerText';

/** Consider the offer "read" a bit before the exact bottom (px). */
const READ_THRESHOLD = 24;

function Check({ on }: { on: boolean }) {
  return (
    <span className={`terms-box${on ? ' terms-box--on' : ''}`}>
      {on && (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}>
          <path d="M5 12l5 5 9-11" />
        </svg>
      )}
    </span>
  );
}

export function TermsScreen() {
  const { markTermsAccepted } = useAuth();
  const [offer, setOffer] = useState(false);
  // Согласие на обработку ПДн — отдельный чекбокс, изначально пустой:
  // объединять его с акцептом оферты нельзя (152-ФЗ, требование юриста).
  const [consent, setConsent] = useState(false);
  const [age, setAge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConsent, setShowConsent] = useState(false);
  // Continue is locked until the user scrolls the offer to the end.
  const [readEnd, setReadEnd] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Degenerate case: content fits without scrolling ⇒ nothing to scroll through.
    const el = cardRef.current;
    if (el && el.scrollHeight <= el.clientHeight + READ_THRESHOLD) setReadEnd(true);
  }, []);

  const onCardScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!readEnd && el.scrollTop + el.clientHeight >= el.scrollHeight - READ_THRESHOLD) {
      setReadEnd(true);
      haptics.tap();
    }
  };

  const confirm = async () => {
    if (!offer || !consent || !age || !readEnd) return;
    setBusy(true);
    setError(null);
    try {
      await acceptTerms();
      haptics.success();
      markTermsAccepted(); // unmounts this screen
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить согласие');
      haptics.error();
      setBusy(false);
    }
  };

  if (showConsent) {
    return <LegalDocOverlay doc="consent" onClose={() => setShowConsent(false)} />;
  }

  return (
    <div className="terms-page">
      <BlueHeader>
        <div className="bhead-title bhead-title--sm">Публичная оферта</div>
      </BlueHeader>

      <div className="content">
      <div className="screen-subtitle" style={{ marginTop: 4 }}>
        Ознакомьтесь с условиями участия и подтвердите согласие.
      </div>

      <div className="terms-card" ref={cardRef} onScroll={onCardScroll}>
        <LegalText text={OFFER_TEXT} />
        <div className="fade-mask" />
      </div>

      <button className="terms-check" onClick={() => setOffer((v) => !v)}>
        <Check on={offer} />
        <span>Я принимаю условия публичной оферты</span>
      </button>
      <button className="terms-check" onClick={() => setConsent((v) => !v)}>
        <Check on={consent} />
        <span>
          Я даю согласие на обработку персональных данных (почта, телефон, никнейм и аватар
          Telegram) в соответствии с{' '}
          <span
            className="terms-link"
            role="link"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              setShowConsent(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setShowConsent(true);
              }
            }}
          >
            Согласием на обработку персональных данных
          </span>
        </span>
      </button>
      <button className="terms-check" onClick={() => setAge((v) => !v)}>
        <Check on={age} />
        <span>Мне исполнилось 18 лет</span>
      </button>

      {error && <div className="field-error auth-error">{error}</div>}

      <button
        className="btn"
        disabled={!offer || !consent || !age || !readEnd || busy}
        onClick={confirm}
      >
        {busy
          ? 'Сохраняем…'
          : readEnd
            ? 'Принять и продолжить'
            : 'Пролистайте оферту до конца'}
      </button>
      <div style={{ height: 24 }} />
      </div>
    </div>
  );
}
